import { canonicalJson, createInitialState, pauseAllPrepRules, planEventIndex, sha256Text, type AppState, type LocalEvent, type PrepPlan, type PrepRule, type PrepTemplate, type Settings, type SyncEntity } from '@autoday/core';
import type { AppStore } from '../store/appStore.ts';
import type { DeviceState, OutboxOp, StorageAdapter } from '../store/storage.ts';
import { randomId } from '../platform.ts';

/**
 * Överföring från gästprofilen till ett konto – ENDAST efter aktivt val ("Lägg till mina lokala
 * aktiviteter i kontot"). Gästens original lämnas orörda. Poster behåller sina ID:n och importidentitet
 * (källa|UID), så ett upprepat försök kan aldrig skapa kopior: servern svarar duplicate/converged,
 * och redan överförda poster hoppas över lokalt. Allt skrivs i kontoprofilen i EN transaktion.
 *
 * Förberedelseplaner (docs/PREPARATION-PLANS.md): mallar följer med som vanliga poster, regler som vanliga
 * poster men PAUSADE. En plan följer med som EN atomisk import (planen + planens egna pass) som synken
 * skickar efter de vanliga posterna; finns redan en plan för samma deadline i kontot skrivs ingenting av
 * den. Planens pass överförs därför aldrig också som vanliga aktiviteter (det skulle ge två uppsättningar).
 */
export interface TransferCounts {
  events: number;
  importedItems: number;
  templates: number;
  notes: number;
  /** Förberedelsemallar. */
  prepTemplates: number;
  /** Regler för automatiska förslag – börjar pausade i kontot. */
  prepRules: number;
  /** Förberedelseplaner (aktiva och avslutade), var och en med sina pass. */
  plans: number;
  total: number;
}

/** En gästöverförd plan: utkorgspost med kind 'prepImport' och doc = { plan, events }. */
export interface PlanBundle {
  plan: PrepPlan;
  events: LocalEvent[];
}

interface Entry {
  key: string;
  entity: SyncEntity;
  id: string;
  doc: unknown;
  importId?: string;
  kind?: 'prepImport';
}

const fp = (doc: unknown) => sha256Text(canonicalJson(doc)).slice(0, 20);

function entries(s: AppState): Entry[] {
  const out: Entry[] = [];
  const planEvents = planEventIndex(s);
  for (const src of Object.values(s.sources)) out.push({ key: `importSource:${src.id}`, entity: 'importSource', id: src.id, doc: src });
  for (const si of Object.values(s.sourceItems)) out.push({ key: `sourceItem:${si.key}`, entity: 'sourceItem', id: si.key, doc: si, importId: si.lastSeenImportId });
  for (const e of Object.values(s.events)) if (!e.deleted && !planEvents.has(e.id)) out.push({ key: `event:${e.id}`, entity: 'event', id: e.id, doc: e });
  for (const o of Object.values(s.overlays)) if (o.notes || o.done || o.categoryId || o.icon) out.push({ key: `overlay:${o.key}`, entity: 'overlay', id: o.key, doc: o });
  for (const t of Object.values(s.templates)) out.push({ key: `template:${t.id}`, entity: 'template', id: t.id, doc: t });
  for (const t of Object.values(s.prepTemplates ?? {})) out.push({ key: `prepTemplate:${t.id}`, entity: 'prepTemplate', id: t.id, doc: t });
  // Regler skapar förslag i kontot först efter ett uttryckligt val där.
  for (const r of Object.values(pauseAllPrepRules(s, 'transferred').prepRules ?? {})) out.push({ key: `prepRule:${r.id}`, entity: 'prepRule', id: r.id, doc: r });
  for (const p of Object.values(s.prepPlans ?? {})) out.push({ key: `prepPlan:${p.id}`, entity: 'prepPlan', id: p.id, doc: planBundle(s, p), kind: 'prepImport' });
  return out;
}

/**
 * Planen och dess egna pass. Ett pass som tagits bort följer med som borttaget: steget är då "borttaget"
 * även i kontot (och kan återställas där). Utan det skulle steget se ut att vänta på synk för alltid.
 */
function planBundle(s: AppState, plan: PrepPlan): PlanBundle {
  const events: LocalEvent[] = [];
  for (const st of plan.steps) {
    const e = s.events[st.eventId];
    if (e) events.push(e);
  }
  return { plan, events };
}

/** Vad i gästprofilen som ännu inte lagts till i just detta konto (eller ändrats sedan dess). */
export function pendingTransfer(s: AppState, d: DeviceState, targetProfileId: string): { entries: Entry[]; counts: TransferCounts } {
  const done = d.guestTransfers?.[targetProfileId]?.keys ?? {};
  const pending = entries(s).filter((e) => done[e.key] !== fp(e.doc));
  const count = (entity: SyncEntity) => pending.filter((e) => e.entity === entity).length;
  const counts: TransferCounts = {
    events: count('event'),
    importedItems: count('sourceItem'),
    templates: count('template'),
    notes: count('overlay'),
    prepTemplates: count('prepTemplate'),
    prepRules: count('prepRule'),
    plans: count('prepPlan'),
    total: 0,
  };
  counts.total = counts.events + counts.importedItems + counts.templates + counts.notes + counts.prepTemplates + counts.prepRules + counts.plans;
  return { entries: pending, counts };
}

function getDoc(s: AppState, e: Entry): unknown {
  switch (e.entity) {
    case 'event':
      return s.events[e.id];
    case 'overlay':
      return s.overlays[e.id];
    case 'template':
      return s.templates[e.id];
    case 'sourceItem':
      return s.sourceItems[e.id];
    case 'importSource':
      return s.sources[e.id];
    case 'prepTemplate':
      return s.prepTemplates?.[e.id];
    case 'prepRule':
      return s.prepRules?.[e.id];
    default:
      return undefined;
  }
}

function putDoc(s: AppState, e: Entry): AppState {
  switch (e.entity) {
    case 'event':
      return { ...s, events: { ...s.events, [e.id]: e.doc as AppState['events'][string] } };
    case 'overlay':
      return { ...s, overlays: { ...s.overlays, [e.id]: e.doc as AppState['overlays'][string] } };
    case 'template':
      return { ...s, templates: { ...s.templates, [e.id]: e.doc as AppState['templates'][string] } };
    case 'sourceItem':
      return { ...s, sourceItems: { ...s.sourceItems, [e.id]: e.doc as AppState['sourceItems'][string] } };
    case 'importSource':
      return { ...s, sources: { ...s.sources, [e.id]: e.doc as AppState['sources'][string] } };
    case 'prepTemplate':
      return { ...s, prepTemplates: { ...s.prepTemplates, [e.id]: e.doc as PrepTemplate } };
    case 'prepRule':
      return { ...s, prepRules: { ...s.prepRules, [e.id]: e.doc as PrepRule } };
    default:
      return s;
  }
}

export function initialAccountState(guestSettings: Settings): AppState {
  const s = createInitialState();
  return { ...s, settings: { ...guestSettings, quietHours: { ...guestSettings.quietHours } } };
}

export interface TransferResult {
  added: number;
  /** Fanns redan i kontot med annat innehåll – lämnades orörda (ingen tyst överskrivning). */
  skipped: number;
  alreadyThere: number;
  /** Förberedelseplaner som läggs till i kontot (atomiskt, med sina pass) när kontot synkas. */
  plans: number;
}

export async function transferGuestToAccount(guest: AppStore, account: StorageAdapter, targetProfileId: string): Promise<TransferResult> {
  const { entries: pending } = pendingTransfer(guest.state, guest.device, targetProfileId);
  let prev = await account.load();
  if (!prev) {
    const init = initialAccountState(guest.state.settings);
    await account.persist(null, init);
    prev = init;
  }
  let next: AppState = prev;
  const ops: OutboxOp[] = [];
  const doneKeys: Record<string, string> = {};
  const now = new Date().toISOString();
  let added = 0;
  let skipped = 0;
  let alreadyThere = 0;
  let plans = 0;
  // Planer från en tidigare överföring som ännu inte nått servern (bunten ligger kvar i kontots utkorg).
  const queued = new Map((await account.outbox()).filter((o) => o.kind === 'prepImport').map((o) => [o.id, fp(o.doc)]));
  for (const e of pending) {
    if (e.kind === 'prepImport') {
      // Kontot har redan en plan för samma deadline – eller en väntande överföring av den: gästens plan
      // läggs inte till igen (inga två uppsättningar).
      const existing = next.prepPlans?.[e.id];
      const waiting = queued.get(e.id);
      if (existing) {
        if (fp(existing) === fp((e.doc as PlanBundle).plan)) alreadyThere++;
        else skipped++;
      } else if (waiting !== undefined) {
        if (waiting === fp(e.doc)) alreadyThere++;
        else skipped++;
      } else {
        // Läggs inte i kontots lokala data nu: planen och passen kommer från servern när importen tagits emot.
        ops.push({ opId: randomId('op_'), createdAt: now, entity: 'prepPlan', id: e.id, baseRevision: null, doc: e.doc, kind: 'prepImport' });
        plans++;
      }
      doneKeys[e.key] = fp(e.doc);
      continue;
    }
    const existing = getDoc(next, e);
    if (existing !== undefined && existing !== null) {
      if (fp(existing) === fp(e.doc)) alreadyThere++;
      else skipped++;
      // Noteras i båda fallen så att samma post inte erbjuds igen vid nästa inloggning.
      doneKeys[e.key] = fp(e.doc);
      continue;
    }
    next = putDoc(next, e);
    ops.push({ opId: randomId('op_'), createdAt: now, entity: e.entity, id: e.id, baseRevision: null, doc: e.doc, ...(e.importId ? { importId: e.importId } : {}) });
    doneKeys[e.key] = fp(e.doc);
    added++;
  }
  // Kategorier och kalendrar som de tillagda posterna behöver (t.ex. en importkälla, en egen kategori eller
  // en förberedelsemalls och plans målkalender).
  let metaChanged = false;
  const calendars = { ...next.calendars };
  const categories = { ...next.categories };
  const needCalendar = (id: string) => {
    if (!calendars[id] && guest.state.calendars[id]) {
      calendars[id] = guest.state.calendars[id]!;
      metaChanged = true;
    }
  };
  const bundles = ops.filter((o) => o.kind === 'prepImport').map((o) => o.doc as PlanBundle);
  for (const ev of [...Object.values(next.events), ...bundles.flatMap((b) => b.events)]) {
    if (!categories[ev.categoryId] && guest.state.categories[ev.categoryId]) {
      categories[ev.categoryId] = guest.state.categories[ev.categoryId]!;
      metaChanged = true;
    }
    needCalendar(ev.calendarId);
  }
  for (const src of Object.values(next.sources)) needCalendar(src.calendarId);
  for (const t of Object.values(next.prepTemplates ?? {})) needCalendar(t.calendarId);
  for (const b of bundles) needCalendar(b.plan.snapshot.calendarId);
  if (metaChanged) {
    next = { ...next, calendars, categories };
    ops.push({ opId: randomId('op_'), createdAt: now, entity: 'meta', id: 'settings', baseRevision: null, doc: { settings: next.settings, calendars, categories } });
  }
  if (ops.length) await account.persist(prev, next, ops);
  // Först när kontoprofilen sparats: notera vad som lagts till (gästens original rörs inte).
  await guest.updateDevice((d) => ({
    ...d,
    guestTransfers: { ...d.guestTransfers, [targetProfileId]: { at: now, keys: { ...(d.guestTransfers?.[targetProfileId]?.keys ?? {}), ...doneKeys } } },
  }));
  return { added, skipped, alreadyThere, plans };
}
